
import React from 'react';
import { SavedResult, TypingMode } from '../types';
import { Trash2, Calendar, Activity, ChevronLeft, BarChart3 } from 'lucide-react';

interface Props {
  results: SavedResult[];
  onClose: () => void;
  onClear: () => void;
  onDelete: (id: string) => void;
}

export const HistoryView: React.FC<Props> = ({ results, onClose, onClear, onDelete }) => {
  const formatDate = (timestamp: number) => {
    try {
      return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(new Date(timestamp));
    } catch (e) {
      return 'Unknown Date';
    }
  };

  const getModeLabel = (result: SavedResult) => {
    if (result.mode === TypingMode.TIME) return `${result.config}s`;
    if (result.mode === TypingMode.WORDS) return `${result.config} words`;
    return result.mode;
  };

  return (
    <div className="w-full max-w-5xl animate-fadeIn">
      <div className="flex justify-between items-center mb-8">
        <button 
          onClick={onClose}
          className="flex items-center space-x-2 text-[#646669] hover:text-[#d1d0c5] transition-colors"
        >
          <ChevronLeft size={20} />
          <span>Back to Test</span>
        </button>
        <h2 className="text-xl font-bold text-[#d1d0c5] flex items-center space-x-2">
          <Activity size={20} className="text-[#e2b714]" />
          <span>Result History</span>
        </h2>
        <button 
          onClick={onClear}
          className="text-xs text-red-500 hover:text-red-400 opacity-60 hover:opacity-100 transition-all flex items-center space-x-1"
        >
          <Trash2 size={14} />
          <span>Clear All</span>
        </button>
      </div>

      {results.length === 0 ? (
        <div className="bg-[#2c2e31] rounded-xl p-12 text-center text-[#646669]">
          <BarChart3 size={48} className="mx-auto mb-4 opacity-20" />
          <p>No results saved yet. Complete a test to see your history!</p>
        </div>
      ) : (
        <div className="bg-[#2c2e31] rounded-xl overflow-hidden shadow-2xl">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="text-[#646669] border-b border-[#323437] uppercase text-[10px] font-bold tracking-widest">
                <th className="px-6 py-4">WPM</th>
                <th className="px-6 py-4">Acc</th>
                <th className="px-6 py-4">Mode</th>
                <th className="px-6 py-4">Lang</th>
                <th className="px-6 py-4">Date</th>
                <th className="px-6 py-4 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#323437]">
              {results.map((result) => (
                <tr key={result.id} className="hover:bg-[#323437] transition-colors group">
                  <td className="px-6 py-4 text-[#e2b714] font-bold text-lg">
                    {result.stats?.wpm ?? 0}
                  </td>
                  <td className="px-6 py-4 text-[#d1d0c5]">
                    {result.stats?.accuracy ?? 0}%
                  </td>
                  <td className="px-6 py-4 text-[#646669] capitalize">
                    {getModeLabel(result)}
                  </td>
                  <td className="px-6 py-4 text-[#646669] uppercase">
                    {result.language?.substring(0, 2) ?? '??'}
                  </td>
                  <td className="px-6 py-4 text-[#646669] text-xs">
                    <div className="flex items-center space-x-1">
                      <Calendar size={12} />
                      <span>{formatDate(result.timestamp)}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button 
                      onClick={() => onDelete(result.id)}
                      className="text-red-500 opacity-0 group-hover:opacity-100 p-2 hover:bg-red-500/10 rounded-lg transition-all"
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
